import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, FileWarning, Flag, Hash, Search, Trash2, UserPlus, Users, X } from 'lucide-react';

import {
  FIELD_SHAPE_CLASS,
  RosterFilters,
  rosterCategoryOf,
  rosterCategoryPatch,
} from '@/components/RosterFilters';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { NO_HOUSE, NOT_ASSIGNED_LABEL } from '@/constants/houses';
import { housesApi } from '@/features/institute/houses/api/houses.api';
import { SetHouseModal } from '@/features/institute/houses/components/SetHouseModal';
import { QueryError } from '@/components/ui/QueryError';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { classesApi } from '@/features/institute/classes/api/classes.api';
import { BulkImportSteps } from '@/features/institute/admissions/components/BulkImportPanel';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords, formatMonth } from '@/lib/format';
import { instituteToday } from '@/features/teacher/utils';
import { cn } from '@/lib/utils';
import type { ImportResult } from '@/types/institute';
import type { StudentListItem, StudentListParams } from '@/types/student';

import { studentsApi } from '../api/students.api';
import { AssignRollNumbersModal } from '../components/AssignRollNumbersModal';
import { StudentExportDialog } from '../components/StudentExportDialog';
import { StudentsRosterTable } from '../components/StudentsRosterTable';
import { StudentsStatRow } from '../components/StudentsStatRow';
import { STUDENTS_PALETTE } from '../theme';
import {
  registerFiltersFrom,
  registerSearchFor,
  type RegisterReturnState,
} from '../utils';
import {
  STUDENTS_PER_PAGE,
  STUDENT_ROWS_PER_PAGE,
  STUDENT_SORT_OPTIONS,
  STUDENT_FILTER_STATUS_OPTIONS,
  SUSPEND_EXPLAINER,
} from '../constants';

/**
 * The student register for the institute's current academic year.
 *
 * Scoped to one session on purpose: a list mixing 2025-26 leavers with 2026-27
 * arrivals answers nobody's question, and every institute's first act each June
 * is to look at *this* year's enrolment.
 *
 * Filtering, sorting and paging all happen on the server — the roll runs to
 * thousands, so the browser is sent one page at a time. The five figures above
 * the table describe the whole session rather than the filtered page, so they
 * do not move as somebody types in the search box.
 *
 * Read-only for anyone holding STUDENT_VIEW without STUDENT_MANAGE, which is
 * what a class teacher gets. The server enforces the same split; this only
 * decides what is worth offering.
 */
export function StudentsPage() {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('STUDENT_MANAGE'));
  const canAdmit = Boolean(permissions?.includes('ADMISSION_CREATE'));

  /** The spreadsheet import, in a dialog so the register stays on screen. */
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  /** Numbering a section's roll — the only way a roll number is given. */
  const [isAssigningRolls, setIsAssigningRolls] = useState(false);

  /**
   * Which register this institute reads.
   *
   * A college has no class, no section and no guardian, so those columns would
   * be dashes on every row. It shows what it actually holds instead.
   */
  const branding = useInstituteBranding((state) => state.branding);
  const isCollege = branding?.institutionType === 'COLLEGE';

  /**
   * The filters live in the address, so a child opened from a filtered list
   * comes back to that list — by the record's Back, the browser's, or a reload.
   *
   * Read once when the page opens; after that the address follows the filters,
   * never the other way round. Links from elsewhere still work the same way:
   * the class roster's intake figure arrives with `classUuid`, the Houses page
   * with `houseUuid` (one house, or `NONE` for everyone still to be placed).
   */
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultFilters = useMemo<StudentListParams>(
    () => ({
      page: 1,
      limit: STUDENTS_PER_PAGE,
      search: '',
      classUuid: '',
      sectionUuid: '',
      status: '',
      gender: '',
      newThisYear: '',
      documentsPending: '',
      houseUuid: '',
      sort: 'name',
      attendanceMonth: instituteToday().slice(0, 7),
    }),
    [],
  );

  const [filters, setFilters] = useState<StudentListParams>(() =>
    registerFiltersFrom(searchParams, defaultFilters),
  );

  const registerSearch = registerSearchFor(filters, defaultFilters).toString();

  // Replaces rather than pushes: every keystroke in the search box is not a
  // page somebody wants to step back through.
  useEffect(() => {
    setSearchParams(new URLSearchParams(registerSearch), { replace: true });
  }, [registerSearch, setSearchParams]);

  /** Opens a child's record, remembering this list for its Back button. */
  function openStudent(student: StudentListItem) {
    const state: RegisterReturnState = {
      registerSearch: registerSearch ? `?${registerSearch}` : '',
    };
    navigate(`/admin/students/${student.uuid}`, { state });
  }

  const [suspending, setSuspending] = useState<StudentListItem | null>(null);
  const [isSuspending, setIsSuspending] = useState(false);
  /** Ticked rows, by uuid. Cleared whenever the list underneath them changes. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isBulkSuspending, setIsBulkSuspending] = useState(false);
  const [isConfirmingBulk, setIsConfirmingBulk] = useState(false);
  /** The students whose house is being set — one row's, or every ticked one. */
  const [houseFor, setHouseFor] = useState<StudentListItem[]>([]);

  // The classes drive the filter row. They fail independently of the register: an
  // institute whose class list is slow should still see its children. A college
  // has none, so the call is skipped rather than answered with an empty list.
  const { data: classes } = useApiQuery(
    () => (isCollege ? Promise.resolve([]) : classesApi.list()),
    [isCollege],
  );

  // For the House filter. Failing on its own, like the classes: the register
  // still loads, and the filter offers only "Not assigned".
  const { data: houseList } = useApiQuery(() => housesApi.list(), []);
  const houseOptions = [
    { value: '', label: 'All houses' },
    // A suspended house still has members, so it can still be filtered on.
    ...(houseList?.houses ?? []).map((house) => ({
      value: house.uuid,
      label: house.status === 'SUSPENDED' ? `${house.name} (suspended)` : house.name,
    })),
    { value: NO_HOUSE, label: NOT_ASSIGNED_LABEL },
  ];
  const houseLabel = (uuid: string) =>
    uuid === NO_HOUSE
      ? NOT_ASSIGNED_LABEL
      : (houseList?.houses.find((house) => house.uuid === uuid)?.name ?? 'Selected house');

  const { data, isLoading, error, refetch } = useApiQuery(
    () => studentsApi.list(filters),
    [
      filters.page,
      filters.limit,
      filters.search,
      filters.classUuid,
      filters.sectionUuid,
      filters.status,
      filters.gender,
      filters.newThisYear,
      filters.documentsPending,
      filters.houseUuid,
      filters.sort,
      filters.attendanceMonth,
      filters.academicYearUuid,
    ],
  );

  // The year filter. The current session is the blank value, so "this year"
  // never goes stale when the school rolls over to the next one.
  const yearOptions = (data?.academicYears ?? []).map((year) => ({
    value: year.isCurrent ? '' : year.uuid,
    label: year.isCurrent ? `${year.name} (current)` : year.name,
  }));

  /**
   * The months the attendance column can be asked for: the session's first to
   * this one, or to the session's last if it has already closed. A month to
   * come has nothing marked in it yet. Whole days, because the picker reaches a
   * month when any day of it falls inside these.
   */
  const today = instituteToday();
  const firstAttendanceDay = data?.academicYear.startDate;
  const sessionLastDay = data?.academicYear.endDate;
  const lastAttendanceDay = sessionLastDay && sessionLastDay < today ? sessionLastDay : today;
  // Blank after a change of year: the server picks that year's month, and says
  // which in its answer.
  const shownMonth = filters.attendanceMonth || data?.attendanceMonth?.month || '';

  /**
   * The register as the office reads it: names, class and section with their
   * first letters raised, once here, so the table, its dialogs and its toasts
   * all say "Anjali Sharma" however the record was typed. Display only — the
   * stored record is untouched.
   */
  const rows = useMemo(
    () =>
      (data?.items ?? []).map((row) => ({
        ...row,
        fullName: capitalizeWords(row.fullName),
        primaryContactName: row.primaryContactName && capitalizeWords(row.primaryContactName),
        className: capitalizeWords(row.className),
        sectionName: capitalizeWords(row.sectionName),
        classTeacherName: row.classTeacherName && capitalizeWords(row.classTeacherName),
      })),
    [data],
  );

  /** What went wrong, in the server's words where it gave any. */
  function reasonFor(caught: unknown): string {
    return caught instanceof ApiRequestError
      ? caught.message
      : 'Something went wrong. Please try again.';
  }

  /**
   * Any change but the page itself sends the reader back to the first page.
   *
   * The ticks go with it: a selection is a set of rows somebody is looking at,
   * and carrying it across a filter change would leave them acting on children
   * that scrolled out of view three filters ago.
   */
  function updateFilters(patch: Partial<StudentListParams>) {
    setFilters((current) => ({ ...current, ...patch, ...(patch.page ? {} : { page: 1 }) }));
    setSelected(new Set());
  }

  const hasFilters = Boolean(
    filters.search ||
      filters.classUuid ||
      filters.sectionUuid ||
      filters.status ||
      filters.gender ||
      filters.newThisYear ||
      filters.documentsPending ||
      filters.houseUuid,
  );

  /** Everything narrowing the roll, cleared together. */
  const SHOW_EVERYONE: Partial<StudentListParams> = {
    search: '',
    classUuid: '',
    sectionUuid: '',
    status: '',
    gender: '',
    newThisYear: '',
    documentsPending: '',
    houseUuid: '',
  };

  function toggleRow(uuid: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  function toggleAll() {
    setSelected((current) =>
      rows.every((row) => current.has(row.uuid)) ? new Set() : new Set(rows.map((row) => row.uuid)),
    );
  }

  /**
   * Sends one student the link that lets them set a password.
   *
   * Opens their account first if admission could not — which is why this is
   * offered on a row reading "No login" as well as one reading "Link not used".
   * No password is sent or shown: they choose their own from the link.
   */
  async function sendInvite(student: StudentListItem) {
    try {
      await studentsApi.resendInvite(student.uuid);
      toast.success(
        `A login link is on its way to ${student.fullName}`,
        'They set their own password with it. Any earlier link has stopped working.',
      );
      // The row's badge is drawn from the account, which has just changed.
      refetch();
    } catch (caught) {
      // The server names the reason — no address on the record, or one that
      // already signs in here — so it is shown verbatim.
      toast.error('Could not send that link', reasonFor(caught));
    }
  }

  /**
   * Sends the child's parent the link into the parent panel.
   *
   * A different account from the student's and a different panel, so a separate
   * action rather than a second address on the same one: for a young child this
   * is the login the family actually uses, and the office decides which of the
   * two it is sending.
   */
  async function sendGuardianInvite(student: StudentListItem) {
    try {
      // The server names the parent it went to — the office wants to know which
      // of a child's contacts was used, and only the server can decrypt it.
      const { sentTo } = await studentsApi.resendGuardianInvite(student.uuid);
      toast.success(
        `A parent login link is on its way to ${sentTo}`,
        'They set their own password with it. Any earlier link has stopped working.',
      );
      refetch();
    } catch (caught) {
      // The server names the reason — no contact with an address, or one that
      // already signs in here — so it is shown verbatim.
      toast.error('Could not send that link', reasonFor(caught));
    }
  }

  async function confirmSuspend() {
    if (!suspending) return;
    const student = suspending;
    setIsSuspending(true);

    try {
      await studentsApi.suspend(student.uuid);
      setSuspending(null);
      toast.success(
        `${student.fullName} is suspended`,
        'They stay on this list and can be reactivated from the row menu.',
      );
      refetch();
    } catch (caught) {
      setSuspending(null);
      toast.error('Could not suspend this student', reasonFor(caught));
    } finally {
      setIsSuspending(false);
    }
  }

  /** No confirmation: reinstating is the undo, and harmless if repeated. */
  async function reinstate(student: StudentListItem) {
    try {
      await studentsApi.reinstate(student.uuid);
      toast.success(`${student.fullName} is back on the register`);
      refetch();
    } catch (caught) {
      toast.error('Could not reactivate this student', reasonFor(caught));
    }
  }

  /**
   * Suspends every ticked student, one request each.
   *
   * Sequential rather than in parallel: this is a write against the roll, and
   * twenty at once is twenty chances to half-succeed in an order nobody can
   * reconstruct. What did and did not go through is reported honestly.
   */
  async function confirmBulkSuspend() {
    const uuids = rows.filter((row) => selected.has(row.uuid)).map((row) => row.uuid);
    setIsBulkSuspending(true);

    let done = 0;
    let firstFailure: string | null = null;

    for (const uuid of uuids) {
      try {
        await studentsApi.suspend(uuid);
        done += 1;
      } catch (caught) {
        firstFailure = firstFailure ?? reasonFor(caught);
      }
    }

    setIsBulkSuspending(false);
    setIsConfirmingBulk(false);
    setSelected(new Set());

    if (firstFailure) {
      toast.error(
        done === 0 ? 'Nobody was suspended' : `${done} of ${uuids.length} suspended`,
        firstFailure,
      );
    } else {
      toast.success(
        `${done} ${done === 1 ? 'student is' : 'students are'} suspended`,
        'They stay on this list and can be reactivated one by one.',
      );
    }

    refetch();
  }

  const selectedCount = rows.filter((row) => selected.has(row.uuid)).length;

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', STUDENTS_PALETTE)}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-[700] tracking-tight text-ink">Students</h1>
          <p className="mt-1 text-sm text-ink-2">
            Manage and view all student information
            {data ? ` for ${data.academicYear.name}` : ''}.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* A college has no sections, so nothing to number. */}
          {canManage && !isCollege && (
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Hash className="h-4 w-4" />}
              className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/60 font-semibold rounded-xl shadow-xs transition-all cursor-pointer active:scale-95"
              onClick={() => setIsAssigningRolls(true)}
            >
              Assign roll numbers
            </Button>
          )}
          {canAdmit && (
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Download className="h-4 w-4" />}
              className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/60 font-semibold rounded-xl shadow-xs transition-all cursor-pointer active:scale-95"
              onClick={() => setIsImporting(true)}
            >
              Import students
            </Button>
          )}
          {/* Waits for the first page: the file is named for its academic year. */}
          {canManage && data && (
            <StudentExportDialog
              pageFilters={filters}
              classes={classes ?? []}
              sessionName={data.academicYear.name}
              isCollege={isCollege}
              houseOptions={houseOptions}
              triggerClassName="h-10 px-4 text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/60 font-semibold rounded-xl shadow-xs transition-all cursor-pointer active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
            />
          )}
          {canAdmit && (
            <button
              type="button"
              onClick={() => navigate('/admin/students/new')}
              className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <UserPlus className="h-4 w-4" />
              <span>Add student</span>
            </button>
          )}
        </div>
      </header>

      {error ? (
        <QueryError title="Could not load the register" error={error} onRetry={refetch} />
      ) : (
        <>
          <StudentsStatRow
            summary={data?.summary ?? null}
            isLoading={isLoading}
            activeCategory={rosterCategoryOf(filters)}
            // Every tile counts students on the roll — active ones — so opening
            // one narrows the list to the same students it counted.
            onSelect={(category) => updateFilters({ ...rosterCategoryPatch(category), status: 'ACTIVE' })}
            attendanceMonth={data?.attendanceMonth ?? null}
            academicYear={data?.academicYear.name}
          />

          {/* Children admitted without their papers — every one brought in from a
              spreadsheet. The count is the whole session's, so it stays put
              while the list below is narrowed to them. */}
          {data && (data.documentsPending > 0 || filters.documentsPending) && (
            <Alert
              tone="warning"
              title={
                data.documentsPending === 0
                  ? 'Every student has their documents on file'
                  : `${data.documentsPending} ${
                      data.documentsPending === 1 ? 'student has' : 'students have'
                    } documents pending`
              }
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p>
                  Students added from a spreadsheet arrive without their certificates. Open a
                  student's record to attach them.
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<FileWarning className="h-3.5 w-3.5" />}
                  onClick={() =>
                    updateFilters({ documentsPending: filters.documentsPending ? '' : 'true' })
                  }
                >
                  {filters.documentsPending ? 'Show every student' : 'Show only these students'}
                </Button>
              </div>
            </Alert>
          )}

          <RosterFilters
            onShowAll={() => updateFilters(SHOW_EVERYONE)}
            filters={filters}
            classes={classes ?? []}
            hasPlacement={!isCollege}
            searchLabel="Search the register"
            searchPlaceholder="Search by name, roll number or admission number…"
            statusLabel="Status"
            statusOptions={STUDENT_FILTER_STATUS_OPTIONS}
            statusPlaceholder="All"
            sortOptions={
              isCollege
                ? STUDENT_SORT_OPTIONS.filter((option) => option.value !== 'rollNumber')
                : STUDENT_SORT_OPTIONS
            }
            onChange={updateFilters}
            hideBorder
            extraFilters={
              filters.houseUuid
                ? [
                    {
                      key: 'house',
                      label: `House: ${houseLabel(filters.houseUuid)}`,
                      onRemove: () => updateFilters({ houseUuid: '' }),
                    },
                  ]
                : []
            }
            extraControls={
              <>
                <Select
                  label="Academic year"
                  labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                  options={yearOptions}
                  className={FIELD_SHAPE_CLASS}
                  value={filters.academicYearUuid ?? ''}
                  disabled={yearOptions.length === 0}
                  onChange={(event) =>
                    updateFilters({
                      academicYearUuid: event.target.value,
                      // Sections belong to one year, so this year's section
                      // matches nobody in another. The attendance month falls
                      // back to the server's choice for that year.
                      sectionUuid: '',
                      attendanceMonth: '',
                    })
                  }
                />
                <Select
                  label="House"
                  labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                  options={houseOptions}
                  className={FIELD_SHAPE_CLASS}
                  value={filters.houseUuid ?? ''}
                  onChange={(event) => updateFilters({ houseUuid: event.target.value })}
                />
              </>
            }
          />

          <div className="card overflow-hidden">
            {/* Not one of the filters: it narrows nobody, it changes which month
                the attendance column and the working-days figure count. */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
              <p className="text-sm text-ink-2">
                Attendance shown for{' '}
                <strong className="font-semibold text-ink">
                  {shownMonth ? formatMonth(`${shownMonth}-01`) : '—'}
                </strong>
              </p>
              <div className="w-44">
                <DatePicker
                  mode="month"
                  label="Attendance month"
                  hideLabel
                  size="sm"
                  value={shownMonth}
                  min={firstAttendanceDay}
                  max={lastAttendanceDay}
                  onChange={(event) => {
                    // The picker has no clear button, but a month is always needed.
                    if (event.target.value) {
                      updateFilters({ attendanceMonth: event.target.value, page: filters.page });
                    }
                  }}
                />
              </div>
            </div>

            {selectedCount > 0 && (
              <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface-2 px-4 py-2.5">
                <p className="text-sm font-[560] text-ink">
                  {selectedCount} {selectedCount === 1 ? 'student' : 'students'} selected
                </p>

                <div className="ml-auto flex items-center gap-2">
                  {canManage && (
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Flag className="h-3.5 w-3.5" />}
                      onClick={() => setHouseFor(rows.filter((row) => selected.has(row.uuid)))}
                    >
                      Set house
                    </Button>
                  )}
                  {canManage && (
                    <Button
                      size="sm"
                      variant="dangerSoft"
                      leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      onClick={() => setIsConfirmingBulk(true)}
                    >
                      Suspend selected
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<X className="h-3.5 w-3.5" />}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}

            <StudentsRosterTable
              rows={rows}
              isLoading={isLoading}
              hasPlacement={!isCollege}
              canManage={canManage}
              selected={selected}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              onOpen={openStudent}
              onEdit={openStudent}
              onResendInvite={(student) => void sendInvite(student)}
              onResendGuardianInvite={(student) => void sendGuardianInvite(student)}
              onSuspend={setSuspending}
              onReinstate={(student) => void reinstate(student)}
              onChangeHouse={(student) => setHouseFor([student])}
              attendanceMonth={filters.attendanceMonth}
              empty={
                hasFilters ? (
                  <EmptyState
                    icon={<Search className="h-5 w-5" />}
                    title="Nothing matches those filters"
                    description="No child on the register matches what you have narrowed it to."
                    action={
                      <Button variant="secondary" onClick={() => updateFilters(SHOW_EVERYONE)}>
                        Clear filters
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    icon={<Users className="h-5 w-5" />}
                    title="Nobody on the register yet"
                    description={
                      canAdmit
                        ? 'Admit a child one at a time, or bring a whole class in from a spreadsheet.'
                        : 'Nobody has been admitted this session yet.'
                    }
                    action={
                      canAdmit ? (
                        <Button onClick={() => navigate('/admin/students/new')}>
                          Admit the first student
                        </Button>
                      ) : undefined
                    }
                  />
                )
              }
            />

            {data && (
              <Pagination
                meta={data.meta}
                itemLabel="students"
                showPageNumbers
                limitOptions={STUDENT_ROWS_PER_PAGE}
                onLimitChange={(limit) => updateFilters({ limit })}
                onPageChange={(page) => updateFilters({ page })}
              />
            )}
          </div>
        </>
      )}

      {(() => {
        const shouldShowDone = Boolean(
          importResult &&
          importResult.admitted > 0 &&
          importResult.rejected === 0
        );

        return (
          <Modal
            isOpen={isImporting}
            onClose={() => {
              setIsImporting(false);
              setImportResult(null);
            }}
            title="Import students from a spreadsheet"
            description="Bring a whole class onto the register in one go."
            size="lg"
            footer={
              shouldShowDone ? (
                <button
                  type="button"
                  onClick={() => {
                    setIsImporting(false);
                    setImportResult(null);
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                >
                  Done
                </button>
              ) : undefined
            }
          >
            {/* The dialog stays open on purpose: the panel reports who could not be
                admitted, and closing over that report would lose it. */}
            {isImporting && (
              <BulkImportSteps
                onImported={() => refetch()}
                onResultChange={setImportResult}
              />
            )}
          </Modal>
        );
      })()}

      <SetHouseModal
        isOpen={houseFor.length > 0}
        students={houseFor.map((student) => ({
          uuid: student.uuid,
          fullName: student.fullName,
          houseUuid: student.house?.uuid ?? null,
        }))}
        onClose={() => setHouseFor([])}
        onSaved={() => {
          setHouseFor([]);
          setSelected(new Set());
          refetch();
        }}
      />

      {canManage && !isCollege && (
        <AssignRollNumbersModal
          isOpen={isAssigningRolls}
          onClose={() => setIsAssigningRolls(false)}
          classes={classes ?? []}
          onAssigned={() => refetch()}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(suspending)}
        title={`Suspend ${suspending?.fullName ?? 'this student'}?`}
        description={SUSPEND_EXPLAINER}
        confirmLabel="Suspend student"
        tone="danger"
        isLoading={isSuspending}
        onConfirm={() => void confirmSuspend()}
        onCancel={() => setSuspending(null)}
      />

      <ConfirmDialog
        isOpen={isConfirmingBulk}
        title={`Suspend ${selectedCount} ${selectedCount === 1 ? 'student' : 'students'}?`}
        description={`${SUSPEND_EXPLAINER} This runs one student at a time and reports anything it could not do.`}
        confirmLabel="Suspend selected"
        tone="danger"
        isLoading={isBulkSuspending}
        onConfirm={() => void confirmBulkSuspend()}
        onCancel={() => setIsConfirmingBulk(false)}
      />
    </div>
  );
}
